import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PharmacySearchAutocomplete } from "@/Common/Components/Controls/PharmacySearchAutocomplete";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { Combobox } from "@/Common/Components/Inputs/Combobox";
import { KendoComboBox } from "@/Common/Components/Inputs/KendoComboBox";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { ConsentPrograms } from "@/Common/Models/ConsentPrograms";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { ConsentProgramHistory } from "@/Common/Models/Patient/ConsentProgramHistory";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientOtherInformationModel } from "@/Common/Models/Patient/UI/Profile/IPatientOtherInformationModel";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectoryPopupController } from "@/ContactDirectory/Components/DirectoryPopupController";
import { FaxDirectorySearchComponent } from "@/ContactDirectory/Components/Search/FaxDirectorySearchComponent";
import { PreferredPharmacyHelper } from "@/ContactDirectory/Helpers/PreferredPharmacyHelper";
import { DirectoryEntityPatientLink, DirectoryPatientLinkUpdate } from "@/ContactDirectory/Models/DirectoryPatientLinkUpdate";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { PreferredPharmacyValidator } from "@/ContactDirectory/Validators/PreferredPharmacyValidator";
import { InsuranceProvider } from "@/Patient/Api/Dto/Responses/InsuranceProvider";
import { LegacyPatientConsentPrograms } from "@/Patient/Components/Consent/LegacyPatientConsentPrograms";
import { PharmacyDropdownItem } from "@/Patient/Models/PharmacyDropdownItem";
import { ConsentProgramDialog } from "../Consent/ConsentProgramDialog";
import { PatientConsentPrograms } from "./ConsentComponents/PatientConsentPrograms";

import styles from "./OtherEdit.module.scss";

interface IPatientProfileOtherProps {
    model: IPatientOtherInformationModel;
    PatientCountryId: string;
}

class OtherEditState {
    public shouldShowAddToDirectoryPopup: boolean = false;
}

class OtherEdit extends BaseReactComponent<IPatientProfileOtherProps, OtherEditState> {
    private preferredPharmacyValidator: PreferredPharmacyValidator;

    constructor(props: IPatientProfileOtherProps) {
        super(props);
        const preferredPharmacyHelper = new PreferredPharmacyHelper(
            patientStore.getCurrentPatient().Addresses,
            sessionStore.getCurrentEmployee()
        );
        this.preferredPharmacyValidator = new PreferredPharmacyValidator(preferredPharmacyHelper);
        this.state = new OtherEditState();
    }

    private closeConsentProgramDialog(): void {
        sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_CloseConsentProgramDialog);
    }

    private savePatientConsent(): void {
        sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_LoadConsentProgramHistory, { PatientId: this.props.model.get("PatientContactId") });

        this.closeConsentProgramDialog();
    }

    public render(): JSX.Element {
        const model: IPatientOtherInformationModel = (this.props.model) ? this.props.model : null;

        const isMexico = sessionStore.getCurrentEmployee().isActiveSiteMexico();

        const insuranceProviders: InsuranceProvider[] = cacheStore.GetInsuranceProviders();
        const insuranceProviderItems = insuranceProviders.map(x => x.ProviderName);

        const patient: Patient = patientStore.getCurrentPatient();
        const person = Person.FromPatient(patient);

        const preferredPharmacy = model.get("PreferredPharmacy");
        const isDirectoryPharmacyLinked = cacheStore.ClinicSettings.EnableContactDirectory && !!preferredPharmacy?.DirectoryPharmacyId;
        const pharmacyInfo = preferredPharmacy?.getPharmacyInfo(false);

        const phoneValidationRule = this.preferredPharmacyValidator.getPhoneValidationRule();
        const phoneMask: string = phoneValidationRule ? phoneValidationRule.EditMaskPattern : "";

        const consentProgramHistory = model.get("ConsentProgramHistory").toArray().map(dto => new ConsentProgramHistory(model.get("PatientContactId"), dto)).toImmutable();

        return (
            <div>
                {this.props.model.get("ConsentProgramPopup") === true && !cacheStore.ClinicSettings.EnableConsentProgramHistory &&
                    this.renderAddPopup()
                }
                {this.props.model.get("ConsentProgramDialog") === true && cacheStore.ClinicSettings.EnableConsentProgramHistory && (
                    <ConsentProgramDialog
                        person={person}
                        onSave={() => this.savePatientConsent()}
                        onClose={() => this.closeConsentProgramDialog()}
                        showAccessWithoutConsent={false}
                    />
                )}
                <div className="patient-prof scrollable">
                    <div className="block separated">
                        <div className="flex-container heading">
                            <div><h3 className="h thin-font">{cacheStore.GetString("PreferredPharmacyLabel")}</h3></div>
                        </div>
                        <div className="psingle-column centered">
                            <table>
                                <tbody>
                                    {cacheStore.ClinicSettings.EnableContactDirectory && (
                                        <tr>
                                            <td className="c1" />
                                            <td className="c2">
                                                <a
                                                    className={styles.addToDirectoryLink}
                                                    onClick={() => this.mutateState(s => s.shouldShowAddToDirectoryPopup = true)}
                                                >
                                                    {this.getString("AddToDirectoryLabel")}
                                                </a>
                                            </td>
                                            <td className="c3" />
                                        </tr>
                                    )}
                                    <tr>
                                        <td className="c1" style={cacheStore.ClinicSettings.EnableContactDirectory ? { verticalAlign: "baseline", paddingTop: "7px" } : {}}>
                                            <label className="form-label">{cacheStore.GetString("PharmacyNameLabel")}</label>
                                        </td>
                                        <td className="c2">
                                            <FeatureFlagControl
                                                flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                                activeFeature={(
                                                    <FaxDirectorySearchComponent
                                                        entityTypes={[DirectoryEntityType.Pharmacy]}
                                                        onItemSelected={item =>
                                                            sessionStore.dispatch(
                                                                LocalPatientOperations.Profile.OtherInfo_PharmacySelect,
                                                                { value: item?.entity }
                                                            )
                                                        }
                                                        clearSearchTextOnSelect={false}
                                                        searchText={pharmacyInfo?.Banner ?? ""}
                                                        searchResultOverride={IsNullOrWhiteSpace(pharmacyInfo?.Banner) ? [] : null}
                                                        onSearchTextChanged={name => sessionStore.dispatch(
                                                            LocalPatientOperations.Profile.OtherInfo_PharmacyBannerChange,
                                                            { value: name }
                                                        )}
                                                        disableSearchField={isDirectoryPharmacyLinked}
                                                        maxLength={250}
                                                        dataCy="contactDirectory-preferredPharmacySearch"
                                                        allowExpandingGeographicSearch
                                                    />
                                                )}
                                                fallbackFeature={(
                                                    <PharmacySearchAutocomplete
                                                        onItemSelectCallback={pharmacy => {
                                                            sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_PharmacySelect, { value: new PharmacyDropdownItem({ Pharmacy: pharmacy }) });
                                                        }}
                                                        componentId="add_preferred_pharmacy"
                                                        onTextChange={value => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_SearchStringChange, { value: value })}
                                                        customPharmacyBanner={model.get("SearchString")}
                                                        maxLength={250}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td className="c3 with-line" rowSpan={12}>
                                            <span style={{ color: "red", fontWeight: "bold", cursor: "pointer" }} onClick={this.OnRemovePharmacy.bind(this)} data-cy="other-pharmacy-remove">X</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="c1">
                                            <label className="form-label">{cacheStore.GetString("PharmacistsLabel")}</label>
                                        </td>
                                        <td className="c2">
                                            <TextboxControl
                                                value={pharmacyInfo?.Name ?? ""}
                                                className="form-control"
                                                operation={LocalPatientOperations.Profile.OtherInfo_PharmacyNameChange}
                                                disabled={isDirectoryPharmacyLinked}
                                                valid={this.preferredPharmacyValidator.validateField("Pharmacists", pharmacyInfo?.Name)}
                                                maxLength={400}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="c1">
                                            <label className="form-label">{cacheStore.GetString("PhoneLabel")}</label>
                                        </td>
                                        <td className="c2">
                                            {isDirectoryPharmacyLinked
                                                ? (
                                                    <TextboxMaskedControl
                                                        value={pharmacyInfo?.Phone ?? ""}
                                                        className="form-control"
                                                        disabled
                                                        mask={phoneMask}
                                                        isValid
                                                    />
                                                )
                                                : ( // don't use mask for custom entries since it can contain the phone extension
                                                    <TextboxControl
                                                        value={pharmacyInfo?.Phone ?? ""}
                                                        className="form-control"
                                                        operation={LocalPatientOperations.Profile.OtherInfo_PharmacyPhoneChange}
                                                        disabled={isDirectoryPharmacyLinked}
                                                        maxLength={250}
                                                    />
                                                )
                                            }
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="c1">
                                            <label className="form-label">{cacheStore.GetString("FaxLabel")}</label>
                                        </td>
                                        <td className="c2">
                                            <TextboxMaskedControl
                                                value={pharmacyInfo?.Fax ?? ""}
                                                className="form-control"
                                                onChangeOperation={LocalPatientOperations.Profile.OtherInfo_PharmacyFaxChange}
                                                disabled={isDirectoryPharmacyLinked}
                                                isValid={this.preferredPharmacyValidator.validateField("Fax", pharmacyInfo?.Fax)}
                                                mask={phoneMask}
                                                allowEmpty={!cacheStore.ClinicSettings.EnableContactDirectory}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="c1">
                                            <label className="form-label">{cacheStore.GetString("AddressLabel")}</label>
                                        </td>
                                        <td className="c2" style={{ paddingBottom: "15px" }}>
                                            <textarea
                                                className="form-control vresize"
                                                rows={3}
                                                value={pharmacyInfo?.Address ?? ""}
                                                onChange={this.onPharmacyAddressChane.bind(this)}
                                                disabled={isDirectoryPharmacyLinked}
                                                maxLength={1000}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="block separated">
                        <div className="flex-container heading">
                            <h3 className="h thin-font">{cacheStore.GetString("InsuranceCompanyLabel")}</h3>
                        </div>

                        <div className="psingle-column centered">
                            <table>
                                <tbody>
                                    <tr>
                                        <td className="c1"><label className="form-label">{cacheStore.GetString("InsuranceCompanyNameLabel")}</label></td>
                                        <td className="c2" data-cy="other-insuranceCompany">
                                            <KendoComboBox controlId="insuranceproviders" items={Immutable.List(insuranceProviderItems)} selectedItem={model.get("InsuranceCompanyName")} changeOperation={LocalPatientOperations.Profile.OtherInfo_InsuranceCompanyNameChange} disabled={false} />
                                        </td>
                                        <td className="c3 with-line" rowSpan={12}>
                                            <span style={{ color: "red", fontWeight: "bold", cursor: "pointer" }} onClick={this.OnRemoveInsuranceProvider.bind(this)} data-cy="other-insurance-remove">X</span>
                                        </td>
                                    </tr>
                                    {!isMexico && (
                                        <tr>
                                            <td className="c1"><label className="form-label">{cacheStore.GetString("PolicyNoLabel")}</label></td>
                                            <td className="c2" style={{ paddingBottom: "15px" }}><TextboxControl value={model.get("InsurancePolicyNo")} className="form-control" operation={LocalPatientOperations.Profile.OtherInfo_InsurancePolicyNoChange} dataCy="other-policyNo" /></td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="block separated">
                        <div className="flex-container heading">
                            <h3 className="thin-font">{cacheStore.GetString("ConsentProgramLabel")}</h3>
                            <div
                                onClick={e => sessionStore.dispatch(cacheStore.ClinicSettings.EnableConsentProgramHistory ? LocalPatientOperations.Profile.OtherInfo_AddConsentProgramDialog : LocalPatientOperations.Profile.OtherInfo_AddConsentPopup, e)}
                                style={{ paddingTop: "10px", paddingRight: "20px" }}
                            >
                                <a>{"[+] " + cacheStore.GetString("AddConsentProgramLabel")}</a>
                            </div>
                        </div>

                        {!cacheStore.ClinicSettings.EnableConsentProgramHistory && (
                            <LegacyPatientConsentPrograms
                                items={model.get("PatientConsentsList")}
                            />
                        )}

                        {cacheStore.ClinicSettings.EnableConsentProgramHistory && (
                            <PatientConsentPrograms
                                patientContactId={model.get("PatientContactId")}
                                items={consentProgramHistory}
                            />
                        )}
                    </div>
                </div>
                {this.state.shouldShowAddToDirectoryPopup && cacheStore.ClinicSettings.EnableContactDirectory && (
                    <DirectoryPopupController
                        onSave={(pharmacy: DirectoryPharmacy) => this.selectDirectoryPharmacy(pharmacy)}
                        onClose={() => this.mutateState(s => s.shouldShowAddToDirectoryPopup = false)}
                        onEntityUseInstead={(pharmacy: DirectoryPharmacy) => this.selectDirectoryPharmacy(pharmacy)}
                        onModify={(pharmacy: DirectoryPharmacy) => this.selectDirectoryPharmacy(pharmacy)}
                        defaultType={DirectoryEntityType.Pharmacy}
                    />
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    private renderAddPopup(): JSX.Element {
        let documentDateMoment = this.props.model.get("DocGivenDate") ? moment(this.props.model.get("DocGivenDate")) : null;
        let revokedDateMoment = this.props.model.get("HasRevokedDate") ? moment(this.props.model.get("HasRevokedDate")) : null;
        let consentedOrRefusedDateMoment = this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate") ? moment(this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate")) : null;

        let isRevokedDateBeforeConsentedDate = false;
        if (revokedDateMoment && consentedOrRefusedDateMoment)
            isRevokedDateBeforeConsentedDate = revokedDateMoment.isBefore(consentedOrRefusedDateMoment, "day");

        let isDateInTheFuture = (documentDateMoment && documentDateMoment.isAfter(moment(), "day"))
            || (revokedDateMoment && revokedDateMoment.isAfter(moment(), "day"))
            || (consentedOrRefusedDateMoment && consentedOrRefusedDateMoment.isAfter(moment(), "day"));
        return (
            <Popup closeTarget={LocalPatientOperations.Profile.OtherInfo_CloseConsentPopup} title={cacheStore.GetString("ConsentProgramLabel")}>
                <div className="p-body">
                    <div className="flex-container">
                        {this.props.model.get("SelectedConsentProgram") == null && (
                            <div style={{ width: "500px" }} className="flex-container">
                                <span style={{ marginTop: "7px", marginRight: "5px" }}>{cacheStore.GetString("ProgramLabel")}</span>
                                <Combobox
                                    HasEmptyItem={true}
                                    id="_TemplateComboBox"
                                    items={this.props.model.get("ConsentPrograms").toArray().map(x => ConsentPrograms.toComboboxItem(x)).toImmutable()}
                                    selectedCode={this.props.model.get("SelectedConsentProgram") != null && this.props.model.get("SelectedConsentProgram").model != null ? this.props.model.get("SelectedConsentProgram").model.Id.toString() : ""}
                                    onChange={this.onConsentProgramChange.bind(this)}
                                />
                            </div>
                        )}
                        {this.props.model.get("SelectedConsentProgram") != null && (
                            <div style={{ width: "500px" }}>
                                <div className="flex-container" style={{ wordBreak: "break-word" }}>
                                    <h1><strong>{this.props.model.get("SelectedConsentProgram").getName()}</strong></h1>
                                </div>
                                <div style={{ maxWidth: "430px", marginLeft: "15px" }}>
                                    {this.props.model.get("SelectedConsentProgram").model.IsDocRequired === true && (
                                        <div className="flex-container" style={{ paddingTop: "10px", paddingBottom: "5px" }}>
                                            <div>
                                                <div className="checkbox">
                                                    <label onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_OnDocGivenClick, e)}>
                                                        <input type="checkbox" checked={this.props.model.get("SelectedConsentProgramDocGiven")} />
                                                        <div>
                                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("NeedDocLabel")}</label>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            {this.props.model.get("DocGivenDate") && this.props.model.get("SelectedConsentProgramDocGiven") === true && (
                                                <div className="flex-container" style={{ paddingTop: "5px", justifyContent: "flex-start" }}>
                                                    <TextboxControlWithDatePicker
                                                        id="_dateDocGiven"
                                                        dateValue={this.props.model.get("DocGivenDate")}
                                                        onChange={dateWithPrecision => this.onDateDocGivenChange(dateWithPrecision)}
                                                        textValue={this.props.model.get("DocGivenDateText")}
                                                    />
                                                    <div style={{ padding: "7.5px" }}>
                                                        <a onClick={this.onTodayClickDocGiven.bind(this)}>{cacheStore.GetString("TodayLabel")}</a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex-container" style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                                        <div style={{ width: "100%" }}>
                                            <strong>{cacheStore.GetString("ChooseAnOptionBelowLabel")}: </strong>
                                            <div className="flex-container" style={{ maxHeight: "30px" }}>
                                                <div className="radio">
                                                    <label onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_OnDidntRespondClick, e)}>
                                                        <input type="radio" checked={this.props.model.get("SelectedConsentProgramDidntRespond")} />
                                                        <div>
                                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("PatientDidntRespondLabel")}</label>
                                                        </div>
                                                    </label>
                                                </div>
                                                {this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate") && this.props.model.get("SelectedConsentProgramDidntRespond") === true && (
                                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                                        <TextboxControlWithDatePicker
                                                            id="_dateConsentedOrRefused"
                                                            dateValue={this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate")}
                                                            onChange={dateWithPrecision => this.onDateConsentRefusedChange(dateWithPrecision)}
                                                            textValue={this.props.model.get("HasConsentedOrRefusedOrDidntRespondDateText")}
                                                        />
                                                        <div style={{ padding: "7.5px" }}>
                                                            <a onClick={this.onTodayClickSelectedConsent.bind(this)}>{cacheStore.GetString("TodayLabel")}</a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex-container" style={{ maxHeight: "30px" }}>
                                                <div className="radio">
                                                    <label onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_OnHasConsentClick, e)}>
                                                        <input type="radio" checked={this.props.model.get("SelectedConsentProgramHasConsented")} />
                                                        <div>
                                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("PatientConsentedLabel")}</label>
                                                        </div>
                                                    </label>
                                                </div>
                                                {this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate") && this.props.model.get("SelectedConsentProgramHasConsented") === true && (
                                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                                        <TextboxControlWithDatePicker
                                                            id="_dateConsentedOrRefused"
                                                            dateValue={this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate")}
                                                            onChange={e => this.onDateConsentRefusedChange(e)}
                                                            textValue={this.props.model.get("HasConsentedOrRefusedOrDidntRespondDateText")}
                                                        />
                                                        <div style={{ padding: "7.5px" }}>
                                                            <a onClick={this.onTodayClickSelectedConsent.bind(this)}>{cacheStore.GetString("TodayLabel")}</a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex-container" style={{ maxHeight: "30px" }}>
                                                <div className="radio">
                                                    <label onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_OnHasRefusedClick, e)}>
                                                        <input type="radio" checked={this.props.model.get("SelectedConsentProgramHasRefused")} />
                                                        <div>
                                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("PatientRefusedLabel")}</label>
                                                        </div>
                                                    </label>
                                                </div>
                                                {this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate")
                                                    && this.props.model.get("SelectedConsentProgramHasRefused") === true
                                                    && (
                                                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                                            <TextboxControlWithDatePicker
                                                                id="_dateConsentedOrRefused"
                                                                dateValue={this.props.model.get("HasConsentedOrRefusedOrDidntRespondDate")}
                                                                onChange={e => this.onDateConsentRefusedChange(e)}
                                                                textValue={this.props.model.get("HasConsentedOrRefusedOrDidntRespondDateText")}
                                                            />
                                                            <div style={{ padding: "7.5px" }}>
                                                                <a onClick={this.onTodayClickSelectedConsent.bind(this)}>{cacheStore.GetString("TodayLabel")}</a>
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {this.props.model.get("SelectedConsentProgramHasConsented") === true && (
                                        <div className="flex-container" style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                                            <div>
                                                <strong>{cacheStore.GetString("SelectIfApplicableLabel")}: </strong>
                                                <div className="checkbox">
                                                    <label onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_OnHasRevokedClick, e)}>
                                                        <input type="checkbox" checked={this.props.model.get("SelectedConsentProgramHasRevoked")} />
                                                        <div>
                                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("PatientRevokeConsentLabel")}</label>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            {this.props.model.get("HasRevokedDate") && this.props.model.get("SelectedConsentProgramHasRevoked") === true && (
                                                <div className="flex-container" style={{ paddingTop: "20px", justifyContent: "flex-start" }}>
                                                    <TextboxControlWithDatePicker
                                                        id="_dateRevoked"
                                                        dateValue={this.props.model.get("HasRevokedDate")}
                                                        onChange={dateTimeWithPrecision => this.onDateConsentRevokedChange(dateTimeWithPrecision)}
                                                        textValue={this.props.model.get("HasRevokedDateText")}
                                                    />
                                                    <div style={{ padding: "7.5px" }}>
                                                        <a onClick={this.onTodayClickConsentRevoked.bind(this)}>{cacheStore.GetString("TodayLabel")}</a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {isRevokedDateBeforeConsentedDate && (
                                        <div className="flex-container">
                                            <p className="label-error">{cacheStore.GetString("ErrorRevokedDateLabel")}</p>
                                        </div>
                                    )}

                                    {isDateInTheFuture && (
                                        <div className="flex-container">
                                            <p className="label-error">{cacheStore.GetString("ErrorFutureDateLabel")}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-footer">
                    <div className="text-right">
                        <button className="btn btn-default push-right-5" onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_CloseConsentPopup, e)}>{cacheStore.GetString("CancelLabel")}</button>
                        {this.props.model.get("IsModifyingConsentProgram") === false &&
                            <button className="btn btn-primary" disabled={this.props.model.get("SelectedConsentProgram") == null || isRevokedDateBeforeConsentedDate || isDateInTheFuture} onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_AddConsentProgramClick, e)}>{cacheStore.GetString("AddLabel")}</button>
                        }
                        {this.props.model.get("IsModifyingConsentProgram") === true &&
                            <button className="btn btn-primary" disabled={this.props.model.get("SelectedConsentProgram") == null || isRevokedDateBeforeConsentedDate || isDateInTheFuture} onClick={e => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_UpdateConsentProgram, e)}>{cacheStore.GetString("SaveLabel")}</button>
                        }
                    </div>
                </div>
            </Popup>
        );
    }

    private async selectDirectoryPharmacy(pharmacy: DirectoryPharmacy): Promise<void> {
        const directoryEntityPatientLink = new DirectoryEntityPatientLink({
            directoryEntity: pharmacy,
            entityType: DirectoryEntityType.Pharmacy,
            patientContactId: this.props.model.get("PatientContactId")
        });

        await DirectoryPatientLinkUpdate.trigger(directoryEntityPatientLink);

        this.mutateState(s => s.shouldShowAddToDirectoryPopup = false);
    }

    private OnRemovePharmacy(): void {
        var operation = new LocalEvent(LocalPatientOperations.Profile.OtherInfo_PharmacyRemove);
        LocalEventDispatcher.dispatch(operation);
    }

    private OnRemoveInsuranceProvider(): void {
        var operation = new LocalEvent(LocalPatientOperations.Profile.OtherInfo_InsuranceRemove);
        LocalEventDispatcher.dispatch(operation);
    }

    private onPharmacyAddressChane(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        var operation = new LocalEvent(LocalPatientOperations.Profile.OtherInfo_PharmacyAddressChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    private onConsentProgramChange(e: string): void {
        var op = new LocalEvent(LocalPatientOperations.Profile.OtherInfo_SelectConsentProgram);
        op.AddItem("value", e);
        LocalEventDispatcher.dispatch(op);
    }

    private onDateDocGivenChange(dateWithPrecision: DateTimeWithPrecision): void {
        var op = new LocalEvent(LocalPatientOperations.Profile.OtherInfo_ChangeDocGivenDate);
        op.AddItem("value", dateWithPrecision);
        LocalEventDispatcher.dispatch(op);
    }

    private onDateConsentRefusedChange(dateWithPrecision: DateTimeWithPrecision): void {
        var op = new LocalEvent(LocalPatientOperations.Profile.OtherInfo_ChangeConsentRefuseDate);
        op.AddItem("value", dateWithPrecision);
        LocalEventDispatcher.dispatch(op);
    }

    private onDateConsentRevokedChange(dateWithPrecision: DateTimeWithPrecision): void {
        var op = new LocalEvent(LocalPatientOperations.Profile.OtherInfo_ChangeConsentRevokeDate);
        op.AddItem("value", dateWithPrecision);
        LocalEventDispatcher.dispatch(op);
    }

    private onTodayClickSelectedConsent(): void {
        LocalEventDispatcher.dispatch(new LocalEvent(LocalPatientOperations.Profile.OtherInfo_TodayClickSelectedConsent));
    }

    private onTodayClickDocGiven(): void {
        LocalEventDispatcher.dispatch(new LocalEvent(LocalPatientOperations.Profile.OtherInfo_TodayClickDocGiven));
    }

    private onTodayClickConsentRevoked(): void {
        LocalEventDispatcher.dispatch(new LocalEvent(LocalPatientOperations.Profile.OtherInfo_TodayClickConsentRevoked));
    }
}

export { IPatientProfileOtherProps, OtherEdit };
